import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  Users, Store, Package, DollarSign, TrendingUp, Shield,
  RefreshCw, CheckCircle, XCircle, Search, ChevronRight,
  MoreHorizontal, Loader, Settings, Layers,
  AlertTriangle, ArrowUpRight, X, BarChart3, Calendar,
  Sparkles, Zap, Globe, Clock, Crown,
  Printer, Calculator, Award, Rocket, Target,
  Activity, ShieldCheck, Smartphone, Boxes, Receipt,
  Download
} from 'lucide-react';
import moment from 'moment';
import 'moment/locale/fr';
import './SuperAdminDashboard.css';

moment.locale('fr');

const PLAN_COLORS = {
  starter:    { bg: '#ecfdf5', color: '#16a34a', border: '#bbf7d0', label: 'Starter' },
  pro:        { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe', label: 'Pro' },
  entreprise: { bg: '#fdf4ff', color: '#9333ea', border: '#e9d5ff', label: 'Entreprise' },
  none:       { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0', label: 'Aucun' },
};

const fmt = (n) => new Intl.NumberFormat('fr-FR', {
  style: 'currency', currency: 'XOF', minimumFractionDigits: 0
}).format(n || 0);

const fmtNum = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);

function getAboStatus(dateFin) {
  if (!dateFin) return { label: 'Aucun', type: 'none' };
  const diff = Math.ceil((new Date(dateFin) - new Date()) / 86400000);
  if (diff < 0)  return { label: 'Expiré',         type: 'expired' };
  if (diff < 7)  return { label: `${diff}j restants`, type: 'warning' };
  return             { label: 'Actif',          type: 'active' };
}

// ── Entreprise detail modal ────────────────────────────────────
function EntrepriseModal({ data, onClose }) {
  if (!data) return null;
  const abo = data.abonnement;
  const s   = data.stats || {};
  const plan = PLAN_COLORS[abo?.plan] || PLAN_COLORS.none;

  return (
    <>
      <div className="sa-overlay" onClick={onClose}/>
      <div className="sa-modal">
        <div className="sa-modal-head">
          <div>
            <h2 className="sa-modal-title">{data.entreprise?.nom}</h2>
            <p className="sa-modal-sub">{data.entreprise?.email}</p>
          </div>
          <button className="sa-modal-close" onClick={onClose}><X size={20}/></button>
        </div>

        <div className="sa-modal-body">
          {/* Plan badge */}
          <div className="sa-modal-plan" style={{ background: plan.bg, border: `1px solid ${plan.border}`, color: plan.color }}>
            <Shield size={16}/>
            <span>Plan <strong>{plan.label}</strong></span>
            {abo && <span className="sa-modal-plan-price">· {fmt(abo.prix)}/mois</span>}
            {abo && (
              <span className={`sa-abo-badge sa-abo-${getAboStatus(abo.dateFin).type}`}>
                {getAboStatus(abo.dateFin).label}
              </span>
            )}
          </div>

          {/* KPIs */}
          <div className="sa-modal-kpis">
            {[
              { label: 'Boutiques',   value: fmtNum(s.boutiques),     color: '#6366f1', bg: '#eef2ff' },
              { label: 'Utilisateurs',value: fmtNum(s.utilisateurs),  color: '#fca311', bg: '#fff8ec' },
              { label: 'Produits',    value: fmtNum(s.produits),      color: '#10b981', bg: '#ecfdf5' },
              { label: 'Ventes',      value: fmtNum(s.ventesTotales), color: '#0ea5e9', bg: '#f0f9ff' },
            ].map((k, i) => (
              <div key={i} className="sa-modal-kpi">
                <div className="sa-modal-kpi-icon" style={{ background: k.bg, color: k.color }}>
                  <BarChart3 size={16}/>
                </div>
                <div className="sa-modal-kpi-val">{k.value}</div>
                <div className="sa-modal-kpi-label">{k.label}</div>
              </div>
            ))}
          </div>

          {/* CA */}
          <div className="sa-modal-ca">
            <DollarSign size={16} color="#fca311"/>
            <span>Chiffre d'affaires total :</span>
            <strong>{fmt(s.chiffreAffaire)}</strong>
          </div>

          {abo && (
            <div className="sa-modal-dates">
              <Calendar size={14} color="#94a3b8"/>
              Abonnement jusqu'au {moment(abo.dateFin).format('D MMMM YYYY')}
              {' '}({moment(abo.dateFin).fromNow()})
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Row actions dropdown ───────────────────────────────────────
function RowMenu({ ent, onStats, onToggle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div className="sa-row-menu" ref={ref}>
      <button className="sa-row-menu-btn" onClick={() => setOpen(v => !v)}>
        <MoreHorizontal size={16}/>
      </button>
      {open && (
        <div className="sa-row-menu-dropdown">
          <button onClick={() => { setOpen(false); onStats(); }}>
            <TrendingUp size={14}/> Voir les stats
          </button>
          <div className="sa-row-menu-divider"/>
          <button
            className={ent.actif ? 'danger' : 'success'}
            onClick={() => { setOpen(false); onToggle(); }}
          >
            {ent.actif ? <><XCircle size={14}/> Désactiver</> : <><CheckCircle size={14}/> Activer</>}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────
export default function SuperAdminDashboard() {
  const navigate = useNavigate();

  const [tab, setTab]                   = useState('overview');
  const [stats, setStats]               = useState(null);
  const [entreprises, setEntreprises]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [refreshing, setRefreshing]     = useState(false);
  const [selectedEnt, setSelectedEnt]   = useState(null);
  const [search, setSearch]             = useState('');
  const [filterPlan, setFilterPlan]     = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Sales pitch
  const [pitch, setPitch]               = useState(null);
  const [pitchLoading, setPitchLoading] = useState(false);
  const [demoMode, setDemoMode]         = useState(false);
  const [roi, setRoi]                   = useState({ produits: 500, caMensuel: 5000000, employes: 5 });
  const [pdfBuilding, setPdfBuilding]   = useState(false);
  const pitchRef                        = useRef(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const [statsRes, entsRes] = await Promise.all([
        axios.get('/api/super-admin/dashboard'),
        axios.get('/api/super-admin/entreprises'),
      ]);
      setStats(statsRes.data.data);
      setEntreprises(entsRes.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (tab === 'pitch' && !pitch && !pitchLoading) loadPitch();
    // eslint-disable-next-line
  }, [tab]);

  const loadPitch = async () => {
    setPitchLoading(true);
    try {
      const { data } = await axios.get('/api/super-admin/sales-pitch');
      setPitch(data.data);
    } catch (e) {
      console.error('Erreur sales pitch:', e);
    } finally {
      setPitchLoading(false);
    }
  };

  const downloadPdf = async () => {
    const node = pitchRef.current;
    if (!node) return;
    setPdfBuilding(true);
    // Marquer pour le CSS qu'on est en mode export (cache toolbar, etc.)
    node.classList.add('sa-pitch--exporting');
    try {
      // Pause pour laisser le repaint s'appliquer
      await new Promise(r => setTimeout(r, 60));

      const canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: '#f1f5f9',
        useCORS: true,
        windowWidth: node.scrollWidth,
        windowHeight: node.scrollHeight,
      });

      const pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4', compress: true });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 18;
      const imgW = pageW - margin * 2;

      // Multi-page : on tranche l'image source en bandes de la hauteur de page
      const pageContentH = pageH - margin * 2;
      const srcSliceH = (canvas.width / imgW) * pageContentH;
      let srcY = 0;
      let firstPage = true;

      while (srcY < canvas.height) {
        const sliceH = Math.min(srcSliceH, canvas.height - srcY);
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceH;
        const ctx = sliceCanvas.getContext('2d');
        ctx.fillStyle = '#f1f5f9';
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(canvas, 0, srcY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
        const sliceDataUrl = sliceCanvas.toDataURL('image/jpeg', 0.92);
        const sliceImgH = (sliceH * imgW) / canvas.width;

        if (!firstPage) pdf.addPage();
        pdf.addImage(sliceDataUrl, 'JPEG', margin, margin, imgW, sliceImgH, undefined, 'FAST');
        firstPage = false;
        srcY += sliceH;
      }

      // Footer sur chaque page : marque + pagination
      const total = pdf.internal.getNumberOfPages();
      for (let p = 1; p <= total; p++) {
        pdf.setPage(p);
        pdf.setFontSize(8);
        pdf.setTextColor(120, 120, 120);
        pdf.text(`GestStock · Argumentaire commercial · ${moment().format('DD/MM/YYYY')}`, margin, pageH - 8);
        pdf.text(`${p} / ${total}`, pageW - margin, pageH - 8, { align: 'right' });
      }

      const fname = `pitch-commercial-geststock-${moment().format('YYYY-MM-DD')}.pdf`;
      pdf.save(fname);
    } catch (e) {
      console.error('Erreur génération PDF:', e);
      alert('Une erreur est survenue lors de la génération du PDF.');
    } finally {
      node.classList.remove('sa-pitch--exporting');
      setPdfBuilding(false);
    }
  };

  const toggleStatus = async (ent) => {
    if (!window.confirm(`${ent.actif ? 'Désactiver' : 'Activer'} "${ent.nom}" ?`)) return;
    try {
      await axios.put(`/api/super-admin/entreprises/${ent._id}/toggle-status`, {
        actif: !ent.actif,
        raison: `${ent.actif ? 'Désactivation' : 'Activation'} manuelle`
      });
      load(true);
    } catch { alert('Erreur'); }
  };

  const viewStats = async (id) => {
    try {
      const { data } = await axios.get(`/api/super-admin/entreprises/${id}/stats`);
      setSelectedEnt(data.data);
    } catch (e) { console.error(e); }
  };

  const filtered = entreprises.filter(e => {
    const matchSearch = !search ||
      e.nom?.toLowerCase().includes(search.toLowerCase()) ||
      e.email?.toLowerCase().includes(search.toLowerCase());
    const matchPlan   = filterPlan   === 'all' || e.abonnement?.plan === filterPlan;
    const matchStatus = filterStatus === 'all' ||
      (filterStatus === 'actif'   && e.actif) ||
      (filterStatus === 'inactif' && !e.actif) ||
      (filterStatus === 'expire'  && e.abonnement && getAboStatus(e.abonnement.dateFin).type === 'expired');
    return matchSearch && matchPlan && matchStatus;
  });

  const plans = ['starter', 'pro', 'entreprise'];
  const planCounts = plans.reduce((acc, p) => {
    acc[p] = entreprises.filter(e => e.abonnement?.plan === p).length;
    return acc;
  }, {});

  if (loading) return (
    <div className="sa-loading">
      <Loader className="sa-spinner" size={32}/>
      <span>Chargement de la plateforme…</span>
    </div>
  );

  const kpis = [
    {
      label: 'Entreprises',
      value: fmtNum(stats?.totalEntreprises || entreprises.length),
      sub: `${entreprises.filter(e => e.actif).length} actives`,
      icon: Store, color: '#6366f1', bg: '#eef2ff'
    },
    {
      label: 'Utilisateurs',
      value: fmtNum(stats?.totalUtilisateurs),
      sub: 'sur la plateforme',
      icon: Users, color: '#fca311', bg: '#fff8ec',
      trend: '+12%'
    },
    {
      label: 'Produits',
      value: fmtNum(stats?.totalProduits),
      sub: 'références actives',
      icon: Package, color: '#10b981', bg: '#ecfdf5'
    },
    {
      label: 'CA Global',
      value: fmt(stats?.chiffreAffaireTotal),
      sub: `${fmtNum(stats?.totalVentes)} ventes`,
      icon: DollarSign, color: '#0ea5e9', bg: '#f0f9ff',
      trend: '+8%'
    },
  ];

  return (
    <div className="sa-page">

      {/* ══ Page Header ══ */}
      <div className="sa-header">
        <div className="sa-header-left">
          <div className="sa-header-icon">
            <Shield size={22}/>
          </div>
          <div>
            <p className="sa-header-date">{moment().format('dddd D MMMM YYYY')}</p>
            <h1 className="sa-header-title">Super Administration</h1>
            <p className="sa-header-sub">Vue globale de la plateforme GestStock</p>
          </div>
        </div>
        <div className="sa-header-right">
          <button className="sa-nav-btn" onClick={() => navigate('/saas-management')}>
            <Settings size={14}/> Gestion SaaS
          </button>
          <button className="sa-nav-btn" onClick={() => navigate('/plans-management')}>
            <Layers size={14}/> Plans
          </button>
          <button className="sa-icon-btn" onClick={() => load(true)} disabled={refreshing}>
            <RefreshCw size={15} className={refreshing ? 'sa-spin' : ''}/>
          </button>
        </div>
      </div>

      {/* ══ Tabs ══ */}
      <div className="sa-tabs">
        {[
          ['overview',    'Vue d\'ensemble',  null],
          ['pitch',       'Pitch commercial', Sparkles],
          ['entreprises', `Entreprises (${entreprises.length})`, null],
          ['abonnements', `Abonnements (${stats?.abonnementsExpires || 0} expirés)`, null],
        ].map(([k, l, Icon]) => (
          <button key={k}
            className={`sa-tab ${tab === k ? 'active' : ''} ${k === 'pitch' ? 'sa-tab--pitch' : ''}`}
            onClick={() => setTab(k)}
          >
            {Icon && <Icon size={13}/>}
            {l}
          </button>
        ))}
      </div>

      {/* ══ Overview Tab ══ */}
      {tab === 'overview' && (
        <div className="sa-content">

          {/* KPI row */}
          <div className="sa-kpi-row">
            {kpis.map((k, i) => {
              const Icon = k.icon;
              return (
                <div key={i} className="sa-kpi" style={{ borderTopColor: k.color }}>
                  <div className="sa-kpi-top">
                    <div className="sa-kpi-icon" style={{ background: k.bg, color: k.color }}>
                      <Icon size={19}/>
                    </div>
                    {k.trend && (
                      <span className="sa-kpi-trend">
                        <ArrowUpRight size={12}/> {k.trend}
                      </span>
                    )}
                  </div>
                  <div className="sa-kpi-val">{k.value}</div>
                  <div className="sa-kpi-label">{k.label}</div>
                  <div className="sa-kpi-sub">{k.sub}</div>
                </div>
              );
            })}
          </div>

          {/* Plans + alerts */}
          <div className="sa-row-2">

            {/* Plans distribution */}
            <div className="sa-card">
              <div className="sa-card-head">
                <div className="sa-card-title">Répartition des plans</div>
                <button className="sa-see-all" onClick={() => navigate('/plans-management')}>
                  Gérer <ChevronRight size={13}/>
                </button>
              </div>

              <div className="sa-plans-grid">
                {plans.map(p => {
                  const pl = PLAN_COLORS[p];
                  const total = entreprises.length || 1;
                  const pct = Math.round((planCounts[p] / total) * 100);
                  return (
                    <div key={p} className="sa-plan-card" style={{ borderColor: pl.border }}>
                      <div className="sa-plan-top">
                        <span className="sa-plan-badge"
                          style={{ background: pl.bg, color: pl.color }}>
                          {pl.label}
                        </span>
                        <span className="sa-plan-count" style={{ color: pl.color }}>
                          {planCounts[p]}
                        </span>
                      </div>
                      <div className="sa-plan-bar-wrap">
                        <div className="sa-plan-bar"
                          style={{ width: `${pct}%`, background: pl.color }}/>
                      </div>
                      <div className="sa-plan-pct">{pct}% des clients</div>
                    </div>
                  );
                })}

                {/* Expirés */}
                <div className="sa-plan-card sa-plan-card--danger">
                  <div className="sa-plan-top">
                    <span className="sa-plan-badge sa-plan-badge--danger">
                      <AlertTriangle size={12}/> Expirés
                    </span>
                    <span className="sa-plan-count sa-plan-count--danger">
                      {stats?.abonnementsExpires || 0}
                    </span>
                  </div>
                  <div className="sa-plan-sub">abonnements à renouveler</div>
                </div>
              </div>
            </div>

            {/* Quick actions + recent */}
            <div className="sa-card">
              <div className="sa-card-head">
                <div className="sa-card-title">Actions rapides</div>
              </div>

              <div className="sa-quick-list">
                {[
                  { label: 'Gérer les abonnements', icon: DollarSign, color: '#fca311', to: '/saas-management' },
                  { label: 'Configurer les plans',   icon: Layers,     color: '#6366f1', to: '/plans-management' },
                  { label: 'Voir les entreprises',   icon: Store,      color: '#10b981', to: null, tab: 'entreprises' },
                  { label: 'Abonnements expirés',    icon: AlertTriangle, color: '#ef4444', to: null, tab: 'abonnements' },
                ].map((a, i) => {
                  const Icon = a.icon;
                  return (
                    <button key={i} className="sa-quick-item"
                      onClick={() => a.to ? navigate(a.to) : setTab(a.tab)}>
                      <div className="sa-quick-icon" style={{ background: a.color + '18', color: a.color }}>
                        <Icon size={16}/>
                      </div>
                      <span>{a.label}</span>
                      <ChevronRight size={14} color="#cbd5e1"/>
                    </button>
                  );
                })}
              </div>

              {/* Active vs Inactive */}
              <div className="sa-status-summary">
                <div className="sa-status-item">
                  <span className="sa-status-dot sa-status-dot--green"/>
                  <span>{entreprises.filter(e => e.actif).length} actives</span>
                </div>
                <div className="sa-status-sep"/>
                <div className="sa-status-item">
                  <span className="sa-status-dot sa-status-dot--red"/>
                  <span>{entreprises.filter(e => !e.actif).length} inactives</span>
                </div>
              </div>
            </div>
          </div>

          {/* Top 5 entreprises */}
          <div className="sa-card">
            <div className="sa-card-head">
              <div className="sa-card-title">Dernières entreprises enregistrées</div>
              <button className="sa-see-all" onClick={() => setTab('entreprises')}>
                Voir tout <ChevronRight size={13}/>
              </button>
            </div>

            <table className="sa-table">
              <thead>
                <tr>
                  <th>Entreprise</th>
                  <th>Plan</th>
                  <th>Boutiques</th>
                  <th>Utilisateurs</th>
                  <th>Statut</th>
                  <th/>
                </tr>
              </thead>
              <tbody>
                {entreprises.slice(0, 5).map(ent => {
                  const pl = PLAN_COLORS[ent.abonnement?.plan] || PLAN_COLORS.none;
                  return (
                    <tr key={ent._id}>
                      <td>
                        <div className="sa-ent-cell">
                          <div className="sa-ent-avatar"
                            style={{ background: pl.bg, color: pl.color }}>
                            {ent.nom?.charAt(0)?.toUpperCase() || 'E'}
                          </div>
                          <div>
                            <div className="sa-ent-name">{ent.nom}</div>
                            <div className="sa-ent-email">{ent.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="sa-plan-pill" style={{ background: pl.bg, color: pl.color }}>
                          {pl.label}
                        </span>
                      </td>
                      <td><span className="sa-num">{ent.stats?.boutiques || 0}</span></td>
                      <td><span className="sa-num">{ent.stats?.utilisateurs || 0}</span></td>
                      <td>
                        <span className={`sa-status-badge ${ent.actif ? 'active' : 'inactive'}`}>
                          {ent.actif ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td>
                        <button className="sa-view-btn" onClick={() => viewStats(ent._id)}>
                          Détails
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══ Pitch commercial Tab ══ */}
      {tab === 'pitch' && (
        <div className="sa-content sa-pitch" ref={pitchRef}>
          {pitchLoading || !pitch ? (
            <div className="sa-pitch-loading">
              <Loader size={28} className="sa-spin"/> Préparation des données…
            </div>
          ) : (() => {
            // Demo mode round-up
            const round = (n, step = 100) => {
              if (!demoMode) return n;
              if (n < 100) return Math.ceil(n);
              return Math.ceil(n / step) * step;
            };
            const t   = pitch.traction || {};
            const r   = pitch.revenu || {};
            const g   = pitch.croissance || {};
            const plansCfg = [
              { key: 'starter',    label: 'Starter',    prix: 100000, boutiques: 1,      utilisateurs: 2,      color: '#16a34a', bg: '#ecfdf5' },
              { key: 'pro',        label: 'Pro',        prix: 150000, boutiques: 3,      utilisateurs: 5,      color: '#2563eb', bg: '#eff6ff' },
              { key: 'entreprise', label: 'Entreprise', prix: 300000, boutiques: '∞',   utilisateurs: '∞',    color: '#9333ea', bg: '#fdf4ff' },
            ];

            // Estimation ROI : 1 produit suivi manuellement = ~30s/jour, soit ~2.5min/semaine = ~12.5h/an
            // Avec geststock : ~1.5s par produit (scan / saisie auto) = ~22x plus rapide
            const heuresGagneesParAn = Math.round((roi.produits * 25) / 60);
            const tauxHoraire = 1500; // XOF/h (estimation employé manutention)
            const economieMainOeuvre = heuresGagneesParAn * tauxHoraire * roi.employes;
            // Perte stock évitée : sans système ~3% de CA en pertes/erreurs, ~0.5% avec système
            const pertesEvitees = Math.round(roi.caMensuel * 12 * 0.025);
            const roiAnnuel = economieMainOeuvre + pertesEvitees;
            const planRecommande = roi.produits > 3000 ? 'entreprise' : roi.produits > 500 ? 'pro' : 'starter';
            const planCfg = plansCfg.find(p => p.key === planRecommande);
            const coutAnnuelPlan = planCfg.prix * 12;
            const roiMultiple = coutAnnuelPlan > 0 ? roiAnnuel / coutAnnuelPlan : 0;

            const salesArgs = [
              { icon: Globe,       title: 'Multi-tenant SaaS',         desc: 'Architecture isolée par entreprise. Chaque client a ses données 100% séparées.', color: '#6366f1' },
              { icon: Store,       title: 'Multi-boutique natif',      desc: `Une seule app pour piloter plusieurs points de vente. ${fmtNum(t.boutiques)} déjà gérés.`, color: '#fca311' },
              { icon: Boxes,       title: 'Stock multi-entrepôts',     desc: 'Suivi en temps réel par entrepôt, transferts traçables, alertes seuil automatiques.', color: '#10b981' },
              { icon: Receipt,     title: 'Caisse + Ventes intégrées', desc: 'Ouverture/clôture journalière, factures PDF, dettes clients, modes de paiement multiples.', color: '#0ea5e9' },
              { icon: ShieldCheck, title: 'Journal complet',           desc: `Chaque mouvement audité : ${fmtNum(t.mouvementsStock)} traces déjà enregistrées.`, color: '#8b5cf6' },
              { icon: Smartphone,  title: 'Code-barres + scan',        desc: 'Import Excel des stocks, lecture/génération code-barres, codes produits auto.', color: '#f97316' },
              { icon: Users,       title: 'Rôles fins',                desc: 'Super-admin, admin entreprise, admin boutique, gestionnaire, caissier — chacun voit ce qui le concerne.', color: '#ec4899' },
              { icon: Activity,    title: 'Tableaux de bord temps réel', desc: 'CA, top produits, alertes stock, retours, dettes — décisions data-driven dès le jour 1.', color: '#06b6d4' },
            ];

            return (
              <>
                {/* Hero — argumentaire chiffres */}
                <div className="sa-pitch-hero">
                  <div className="sa-pitch-hero-left">
                    <div className="sa-pitch-tagline">
                      <Rocket size={14}/> Argumentaire commercial — GestStock SaaS
                    </div>
                    <h2 className="sa-pitch-title">
                      Une plateforme qui prouve sa valeur <span>par les chiffres</span>
                    </h2>
                    <p className="sa-pitch-lede">
                      Voici les chiffres réels de la plateforme à présenter en rendez-vous.
                      Activez le <strong>mode démo</strong> pour arrondir les nombres lors d'une présentation.
                    </p>
                    <div className="sa-pitch-toolbar">
                      <label className="sa-demo-toggle">
                        <input type="checkbox" checked={demoMode} onChange={e => setDemoMode(e.target.checked)}/>
                        <span/> Mode démo (chiffres arrondis)
                      </label>
                      <button className="sa-print-btn sa-pdf-btn" onClick={downloadPdf} disabled={pdfBuilding}>
                        {pdfBuilding
                          ? <><Loader size={13} className="sa-spin"/> Génération…</>
                          : <><Download size={13}/> Télécharger PDF</>}
                      </button>
                      <button className="sa-print-btn" onClick={() => window.print()}>
                        <Printer size={13}/> Imprimer
                      </button>
                    </div>
                  </div>
                  <div className="sa-pitch-hero-right">
                    <Award size={64} strokeWidth={1.5}/>
                  </div>
                </div>

                {/* Big numbers — traction */}
                <div className="sa-pitch-section">
                  <div className="sa-pitch-section-head">
                    <Sparkles size={14}/> Traction de la plateforme
                  </div>
                  <div className="sa-pitch-bignums">
                    {[
                      { val: fmtNum(round(t.entreprises)), label: 'Entreprises clientes', sub: `${fmtNum(t.entreprisesActives)} actives`, icon: Store, color: '#6366f1' },
                      { val: fmtNum(round(t.boutiques)),    label: 'Boutiques pilotées', sub: 'sur la plateforme', icon: Store, color: '#fca311' },
                      { val: fmtNum(round(t.utilisateurs)), label: 'Utilisateurs actifs', sub: 'employés équipés', icon: Users, color: '#10b981' },
                      { val: fmtNum(round(t.produits)),     label: 'Produits suivis', sub: 'références gérées', icon: Package, color: '#0ea5e9' },
                      { val: fmtNum(round(t.ventes)),       label: 'Transactions traitées', sub: 'depuis le lancement', icon: Receipt, color: '#ec4899' },
                      { val: fmt(round(t.chiffreAffairesTraite, 100000)), label: "CA traité", sub: 'par nos clients', icon: DollarSign, color: '#8b5cf6' },
                      { val: fmtNum(round(t.mouvementsStock)),  label: 'Mouvements de stock', sub: 'journal complet', icon: Activity, color: '#f97316' },
                      { val: `${(t.retentionPct || 0).toFixed(0)}%`, label: 'Rétention clients', sub: `${fmtNum(t.entreprisesActives)}/${fmtNum(t.entreprises)} restent actifs`, icon: ShieldCheck, color: '#16a34a' },
                    ].map((k, i) => {
                      const I = k.icon;
                      return (
                        <div key={i} className="sa-bignum" style={{ borderTopColor: k.color }}>
                          <div className="sa-bignum-icon" style={{ background: k.color + '18', color: k.color }}>
                            <I size={16}/>
                          </div>
                          <div className="sa-bignum-val">{k.val}</div>
                          <div className="sa-bignum-label">{k.label}</div>
                          <div className="sa-bignum-sub">{k.sub}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* MRR / ARR */}
                <div className="sa-pitch-section">
                  <div className="sa-pitch-section-head">
                    <Crown size={14}/> Revenus récurrents (MRR / ARR)
                  </div>
                  <div className="sa-mrr-row">
                    <div className="sa-mrr-card sa-mrr-card--mrr">
                      <div className="sa-mrr-label">MRR · Revenu récurrent mensuel</div>
                      <div className="sa-mrr-val">{fmt(r.mrr)}</div>
                      <div className="sa-mrr-sub">{r.abonnementsActifs} abonnements actifs</div>
                    </div>
                    <div className="sa-mrr-card sa-mrr-card--arr">
                      <div className="sa-mrr-label">ARR · Revenu récurrent annuel</div>
                      <div className="sa-mrr-val">{fmt(r.arr)}</div>
                      <div className="sa-mrr-sub">Projection 12 mois</div>
                    </div>
                    <div className="sa-mrr-card sa-mrr-card--growth">
                      <div className="sa-mrr-label">Croissance ventes 30j</div>
                      <div className="sa-mrr-val" style={{ color: (g.growthPct || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                        {g.growthPct === null ? '—' : `${g.growthPct >= 0 ? '+' : ''}${g.growthPct.toFixed(1)}%`}
                      </div>
                      <div className="sa-mrr-sub">vs. 30j précédents</div>
                    </div>
                    <div className="sa-mrr-card sa-mrr-card--new">
                      <div className="sa-mrr-label">Nouveaux clients (30j)</div>
                      <div className="sa-mrr-val">+{fmtNum(g.nouveauxClients || 0)}</div>
                      <div className="sa-mrr-sub">
                        {g.growthClientsPct === null
                          ? `vs ${g.nouveauxClientsPrec || 0} prec.`
                          : `${g.growthClientsPct >= 0 ? '+' : ''}${g.growthClientsPct.toFixed(0)}% vs période préc.`}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Arguments clés */}
                <div className="sa-pitch-section">
                  <div className="sa-pitch-section-head">
                    <Zap size={14}/> 8 arguments de vente solides
                  </div>
                  <div className="sa-args-grid">
                    {salesArgs.map((a, i) => {
                      const I = a.icon;
                      return (
                        <div key={i} className="sa-arg-card" style={{ borderLeftColor: a.color }}>
                          <div className="sa-arg-icon" style={{ background: a.color + '18', color: a.color }}>
                            <I size={18}/>
                          </div>
                          <div>
                            <div className="sa-arg-title">{a.title}</div>
                            <div className="sa-arg-desc">{a.desc}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Plans comparison */}
                <div className="sa-pitch-section">
                  <div className="sa-pitch-section-head">
                    <Layers size={14}/> Comparatif des plans
                  </div>
                  <div className="sa-plans-matrix">
                    {plansCfg.map(p => {
                      const recommended = p.key === 'pro';
                      const adoption = r.plansDistribution?.[p.key] || 0;
                      const mrrPlan  = r.mrrParPlan?.[p.key] || 0;
                      return (
                        <div key={p.key} className={`sa-plan-tile ${recommended ? 'sa-plan-tile--reco' : ''}`} style={{ borderColor: p.color + '40' }}>
                          {recommended && <div className="sa-plan-ribbon"><Crown size={11}/> Le plus populaire</div>}
                          <div className="sa-plan-name" style={{ color: p.color }}>{p.label}</div>
                          <div className="sa-plan-price">
                            <strong>{fmt(p.prix)}</strong><span>/mois</span>
                          </div>
                          <ul className="sa-plan-features">
                            <li><CheckCircle size={12} color={p.color}/> {p.boutiques} boutique{p.boutiques !== 1 ? 's' : ''}</li>
                            <li><CheckCircle size={12} color={p.color}/> {p.utilisateurs} utilisateurs</li>
                            <li><CheckCircle size={12} color={p.color}/> Stock & ventes illimités</li>
                            <li><CheckCircle size={12} color={p.color}/> Journal complet, audit</li>
                            <li><CheckCircle size={12} color={p.color}/> Imports Excel</li>
                            {p.key !== 'starter' && <li><CheckCircle size={12} color={p.color}/> Multi-entrepôts</li>}
                            {p.key === 'entreprise' && <li><CheckCircle size={12} color={p.color}/> Support prioritaire</li>}
                          </ul>
                          <div className="sa-plan-stats">
                            <span><strong>{adoption}</strong> client{adoption !== 1 ? 's' : ''}</span>
                            <span className="sa-plan-mrr">{fmt(mrrPlan)} MRR</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* ROI Calculator */}
                <div className="sa-pitch-section">
                  <div className="sa-pitch-section-head">
                    <Calculator size={14}/> Calculateur de ROI — à présenter au prospect
                  </div>
                  <div className="sa-roi-wrap">
                    <div className="sa-roi-inputs">
                      <div className="sa-roi-field">
                        <label>Nombre de produits gérés</label>
                        <input type="number" min="0" step="100" value={roi.produits}
                          onChange={e => setRoi({ ...roi, produits: parseInt(e.target.value) || 0 })}/>
                      </div>
                      <div className="sa-roi-field">
                        <label>CA mensuel estimé (XOF)</label>
                        <input type="number" min="0" step="100000" value={roi.caMensuel}
                          onChange={e => setRoi({ ...roi, caMensuel: parseInt(e.target.value) || 0 })}/>
                      </div>
                      <div className="sa-roi-field">
                        <label>Nombre d'employés concernés</label>
                        <input type="number" min="1" step="1" value={roi.employes}
                          onChange={e => setRoi({ ...roi, employes: parseInt(e.target.value) || 1 })}/>
                      </div>
                    </div>
                    <div className="sa-roi-results">
                      <div className="sa-roi-row">
                        <Clock size={14} color="#fca311"/>
                        <span>Temps gagné / an</span>
                        <strong>{fmtNum(heuresGagneesParAn * roi.employes)} h</strong>
                      </div>
                      <div className="sa-roi-row">
                        <DollarSign size={14} color="#10b981"/>
                        <span>Économie main-d'œuvre / an</span>
                        <strong>{fmt(economieMainOeuvre)}</strong>
                      </div>
                      <div className="sa-roi-row">
                        <ShieldCheck size={14} color="#6366f1"/>
                        <span>Pertes de stock évitées / an</span>
                        <strong>{fmt(pertesEvitees)}</strong>
                      </div>
                      <div className="sa-roi-divider"/>
                      <div className="sa-roi-row sa-roi-row--total">
                        <Target size={14}/>
                        <span>Bénéfice annuel total estimé</span>
                        <strong>{fmt(roiAnnuel)}</strong>
                      </div>
                      <div className="sa-roi-pitch">
                        <Rocket size={14}/>
                        <div>
                          Plan recommandé : <strong style={{ color: planCfg.color }}>{planCfg.label}</strong> · {fmt(planCfg.prix)}/mois
                          <div className="sa-roi-mult">
                            <strong>ROI ×{roiMultiple.toFixed(1)}</strong>
                            <span> — investissement remboursé en {Math.max(1, Math.ceil(coutAnnuelPlan / Math.max(1, roiAnnuel / 12)))} mois</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top clients + recent signups */}
                <div className="sa-pitch-section sa-pitch-section--split">
                  <div className="sa-card">
                    <div className="sa-card-head">
                      <div className="sa-card-title"><Award size={14}/> Top clients (par CA)</div>
                    </div>
                    {(pitch.topClients || []).length === 0 ? (
                      <p className="sa-pitch-empty">Aucune donnée disponible</p>
                    ) : (
                      <ul className="sa-top-clients">
                        {pitch.topClients.map((c, i) => (
                          <li key={i}>
                            <span className="sa-top-rank">#{i + 1}</span>
                            <span className="sa-top-name">{c.entreprise || 'Entreprise'}</span>
                            <span className="sa-top-ca">{fmt(c.ca)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="sa-card">
                    <div className="sa-card-head">
                      <div className="sa-card-title"><Rocket size={14}/> Derniers clients signés</div>
                    </div>
                    {(pitch.recentSignups || []).length === 0 ? (
                      <p className="sa-pitch-empty">Aucun client récent</p>
                    ) : (
                      <ul className="sa-recent-signups">
                        {pitch.recentSignups.map(e => (
                          <li key={e._id}>
                            <span className={`sa-signup-dot ${e.actif ? 'on' : 'off'}`}/>
                            <span className="sa-signup-name">{e.nom}</span>
                            <span className="sa-signup-date">
                              {moment(e.createdAt).fromNow()}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* ══ Entreprises Tab ══ */}
      {tab === 'entreprises' && (
        <div className="sa-content">

          {/* Filters bar */}
          <div className="sa-filters">
            <div className="sa-search-wrap">
              <Search size={15} className="sa-search-icon"/>
              <input
                className="sa-search"
                placeholder="Rechercher une entreprise…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select className="sa-select" value={filterPlan} onChange={e => setFilterPlan(e.target.value)}>
              <option value="all">Tous les plans</option>
              {plans.map(p => <option key={p} value={p}>{PLAN_COLORS[p].label}</option>)}
            </select>
            <select className="sa-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="all">Tous statuts</option>
              <option value="actif">Actifs</option>
              <option value="inactif">Inactifs</option>
              <option value="expire">Expirés</option>
            </select>
            <span className="sa-filter-count">{filtered.length} résultat{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          <div className="sa-card sa-card--table">
            <table className="sa-table">
              <thead>
                <tr>
                  <th>Entreprise</th>
                  <th>Plan</th>
                  <th>Abonnement</th>
                  <th>Boutiques</th>
                  <th>Utilisateurs</th>
                  <th>Produits</th>
                  <th>CA</th>
                  <th>Statut</th>
                  <th/>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan="9" className="sa-empty-row">Aucune entreprise trouvée</td></tr>
                ) : filtered.map(ent => {
                  const pl  = PLAN_COLORS[ent.abonnement?.plan] || PLAN_COLORS.none;
                  const abo = getAboStatus(ent.abonnement?.dateFin);
                  return (
                    <tr key={ent._id} className={!ent.actif ? 'sa-row--inactive' : ''}>
                      <td>
                        <div className="sa-ent-cell">
                          <div className="sa-ent-avatar" style={{ background: pl.bg, color: pl.color }}>
                            {ent.nom?.charAt(0)?.toUpperCase() || 'E'}
                          </div>
                          <div>
                            <div className="sa-ent-name">{ent.nom}</div>
                            <div className="sa-ent-email">{ent.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="sa-plan-pill" style={{ background: pl.bg, color: pl.color }}>
                          {pl.label}
                        </span>
                      </td>
                      <td>
                        <div>
                          <span className={`sa-abo-badge sa-abo-${abo.type}`}>{abo.label}</span>
                          {ent.abonnement?.dateFin && (
                            <div className="sa-abo-date">
                              {moment(ent.abonnement.dateFin).format('DD/MM/YYYY')}
                            </div>
                          )}
                        </div>
                      </td>
                      <td><span className="sa-num">{ent.stats?.boutiques || 0}</span></td>
                      <td><span className="sa-num">{ent.stats?.utilisateurs || 0}</span></td>
                      <td><span className="sa-num">{fmtNum(ent.stats?.produits || 0)}</span></td>
                      <td>
                        <span className="sa-ca">{fmt(ent.stats?.chiffreAffaire || 0)}</span>
                      </td>
                      <td>
                        <span className={`sa-status-badge ${ent.actif ? 'active' : 'inactive'}`}>
                          {ent.actif ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td>
                        <RowMenu ent={ent} onStats={() => viewStats(ent._id)} onToggle={() => toggleStatus(ent)}/>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══ Abonnements Tab ══ */}
      {tab === 'abonnements' && (
        <div className="sa-content">
          <div className="sa-abo-grid">
            {/* Plans summary */}
            {plans.map(p => {
              const pl   = PLAN_COLORS[p];
              const list = entreprises.filter(e => e.abonnement?.plan === p);
              return (
                <div key={p} className="sa-card">
                  <div className="sa-card-head">
                    <div>
                      <span className="sa-plan-pill" style={{ background: pl.bg, color: pl.color }}>
                        {pl.label}
                      </span>
                      <div className="sa-card-title" style={{ marginTop: 8 }}>
                        {list.length} entreprise{list.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  <div className="sa-abo-list">
                    {list.length === 0 ? (
                      <p className="sa-abo-empty">Aucune entreprise sur ce plan</p>
                    ) : list.map(e => {
                      const abo = getAboStatus(e.abonnement?.dateFin);
                      return (
                        <div key={e._id} className="sa-abo-item">
                          <div className="sa-abo-item-name">{e.nom}</div>
                          <div className="sa-abo-item-right">
                            <span className={`sa-abo-badge sa-abo-${abo.type}`}>{abo.label}</span>
                            <button className="sa-view-btn" onClick={() => viewStats(e._id)}>→</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Expirés */}
            <div className="sa-card sa-card--danger">
              <div className="sa-card-head">
                <div>
                  <span className="sa-plan-pill sa-plan-pill--danger">
                    <AlertTriangle size={12}/> Expirés
                  </span>
                  <div className="sa-card-title" style={{ marginTop: 8 }}>
                    {stats?.abonnementsExpires || 0} à renouveler
                  </div>
                </div>
                <button className="sa-see-all" onClick={() => navigate('/saas-management')}>
                  Gérer <ChevronRight size={13}/>
                </button>
              </div>
              <div className="sa-abo-list">
                {entreprises
                  .filter(e => e.abonnement && getAboStatus(e.abonnement.dateFin).type === 'expired')
                  .map(e => (
                    <div key={e._id} className="sa-abo-item">
                      <div className="sa-abo-item-name">{e.nom}</div>
                      <div className="sa-abo-item-right">
                        <span className="sa-abo-badge sa-abo-expired">Expiré</span>
                        <button className="sa-view-btn" onClick={() => viewStats(e._id)}>→</button>
                      </div>
                    </div>
                  ))}
                {(stats?.abonnementsExpires || 0) === 0 && (
                  <p className="sa-abo-empty">✓ Aucun abonnement expiré</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ Modal detail ══ */}
      {selectedEnt && <EntrepriseModal data={selectedEnt} onClose={() => setSelectedEnt(null)}/>}
    </div>
  );
}
